import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Trash2, Upload, Download, MessageCircle, Send, Calendar, Users, Eye, Edit3, Archive, Tag as TagIcon } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import UserAvatar from './UserAvatar';
import remarkGfm from 'remark-gfm';
import { MentionTextarea, mentionMarkdownComponents } from './Mentions';
import { celebrate, popElement } from './Celebration';
import SubtaskList from './SubtaskList';
import TagPicker from './TagPicker';
import HorizonTaskSection from './HorizonTaskSection';
import './TaskModal.css';
import { mediaUrl, absoluteMediaUrl } from '../media';
import BstId from './BstId';

function TaskModal({ task, project, onClose, onUpdate, onDelete, onArchive, onCreateTag, focusSection = null, horizonId, onHorizonScheduleChange }) {
  const [editedTask, setEditedTask] = useState({
    ...task,
    subtasks: task.subtasks || [],
    tags: (task.tags || []).map(t => String(t?._id || t))
  });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);
  const [subtaskError, setSubtaskError] = useState(null);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');

  // Deep-link target. A comment notification opens the task with
  // focusSection='comments'; TaskModal has no tabs, so we scroll the
  // conversation into view instead.
  const commentsRef = useRef(null);

  // Comment composer: auto-grows with content up to a cap, then scrolls
  // internally. The floor is CSS min-height, so shrinking back after a post
  // is handled by simply recomputing on an empty value.
  const commentFieldRef = useRef(null);
  const COMMENT_MAX_HEIGHT = 200;

  // The celebration's origin when a task is finished from here (WR-009).
  const statusControlRef = useRef(null);
  useEffect(() => {
    const el = commentFieldRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMMENT_MAX_HEIGHT)}px`;
  }, [newComment]);
  useEffect(() => {
    if (focusSection !== 'comments' || !commentsRef.current) return;
    // Defer past the modal's entry animation and the comments fetch so the
    // section has its final height before we scroll to it.
    const t = setTimeout(() => {
      commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(t);
  }, [focusSection, comments.length]);

  // Members pool for mentions: project members plus the owner.
  const mentionMembers = React.useMemo(() => {
    const pool = [...(project?.members || [])];
    if (project?.owner && !pool.some(m => m._id === project.owner._id)) {
      pool.unshift(project.owner);
    }
    return pool;
  }, [project]);

  useEffect(() => {
    fetchComments();
  }, [task._id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showAssigneeDropdown && !e.target.closest('.assignee-dropdown-wrapper')) {
        setShowAssigneeDropdown(false);
        setAssigneeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssigneeDropdown]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`/comments/task/${task._id}`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);

    // Compared against the task as it was loaded, not against render state:
    // this must fire on the transition, never on saving a task that was
    // already done (WR-009).
    const becameDone = task.status !== 'done' && editedTask.status === 'done';
    // Read the rect before the save closes the modal and the node goes away.
    const originRect = becameDone
      ? statusControlRef.current?.getBoundingClientRect()
      : null;

    try {
      await onUpdate(task._id, editedTask);
      // Only after the server accepted it — a rejected save never celebrates.
      if (becameDone) {
        celebrate(originRect);
        popElement(statusControlRef.current);
      }
      onClose();
    } catch (error) {
      console.error('Failed to update task:', error);
    } finally {
      setLoading(false);
    }
  };

  // Archive this one finished task (WR-008). Only offered for done tasks —
  // the server refuses anything else — and always reversible from the
  // board's Completed view.
  const handleArchive = async () => {
    const ok = window.confirm(
      `Archive "${task.title}"?\n\n` +
      'It leaves the board but is kept, and can be restored any time from Completed.'
    );
    if (!ok) return;
    try {
      await onArchive(task._id);
    } catch (error) {
      console.error('Failed to archive task:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await onDelete(task._id);
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(`/tasks/${task._id}/upload`, formData);
      setEditedTask(response.data);
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const response = await axios.delete(`/tasks/${task._id}/attachments/${attachmentId}`);
      setEditedTask(response.data);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    }
  };

  const handleAddComment = async (e) => {
    e?.preventDefault();
    if (!newComment.trim()) return;

    try {
      const response = await axios.post('/comments', {
        content: newComment,
        task: task._id
      });
      setComments([response.data, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  // Enter inserts a newline (textarea default); Ctrl/Cmd+Enter posts.
  //
  // Note this never fires while the mention dropdown is open: Mentions.js
  // calls preventDefault() on Enter to pick the highlighted user and only
  // forwards the event onward when it did not handle it.
  const handleCommentKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const toggleAssignee = (userId) => {
    setEditedTask({
      ...editedTask,
      assignedTo: editedTask.assignedTo.some(u => u._id === userId)
        ? editedTask.assignedTo.filter(u => u._id !== userId)
        : [...editedTask.assignedTo, project.members.find(m => m._id === userId)]
    });
  };

  const removeAssignee = (userId) => {
    setEditedTask({
      ...editedTask,
      assignedTo: editedTask.assignedTo.filter(u => u._id !== userId)
    });
  };

  const filteredMembers = project.members?.filter(m =>
    m.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  ) || [];

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  /**
   * Subtask changes (WR-011).
   *
   * Adding, deleting, cycling a status, assigning or dating a subtask all
   * save straight away, as they did before this WR — those are discrete
   * choices, and a subtask assignment that only lands on "Save Changes"
   * would never notify anyone if the modal were dismissed. Typing a title is
   * the exception: it holds locally and commits on blur, so an edit is one
   * request rather than one per keystroke.
   *
   * `subtaskError` surfaces the server's rejection when an assignee is not a
   * project member — the one thing the client can get wrong here.
   */
  const handleSubtasksChange = async (subtasks, persist) => {
    const updated = { ...editedTask, subtasks };
    setEditedTask(updated);
    if (!persist) return;
    setSubtaskError(null);
    try {
      const saved = await onUpdate(task._id, updated);
      // Take the server's subtasks back. Two things depend on it: a row
      // added here has no _id until the server mints one, and without that
      // id the next save looks like a brand-new subtask — which would
      // re-notify its assignee on every edit. The response also carries the
      // populated assignee the row draws its avatar from.
      if (saved?.subtasks) {
        setEditedTask(prev => ({ ...prev, subtasks: saved.subtasks }));
      }
    } catch (error) {
      setSubtaskError(error.response?.data?.error || 'Failed to save subtask.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content task-modal" onClick={e => e.stopPropagation()}>
        <div className="task-modal-header">
          <div className="task-modal-heading">
            <h2>Task details</h2>
            {/* Click to copy — this is where someone wants the id to paste
                into a message (WR-013 §4). */}
            <BstId id={editedTask.bstId} copyable />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {onArchive && task.status === 'done' && !task.archived && (
              <button onClick={handleArchive} className="btn btn-secondary" style={{ padding: '8px 16px' }} title="Archive this task">
                <Archive size={18} />
              </button>
            )}
            <button onClick={handleDelete} className="btn btn-danger" style={{ padding: '8px 16px' }}>
              <Trash2 size={18} />
            </button>
            <button onClick={onClose} className="btn btn-ghost" style={{ padding: '8px', width: '40px', height: '40px' }}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="task-modal-body">
          <div className="task-modal-main">
            <div className="input-group">
              <label>Title</label>
              <input
                type="text"
                value={editedTask.title}
                onChange={e => setEditedTask({ ...editedTask, title: e.target.value })}
              />
            </div>

            <div className="input-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ marginBottom: 0 }}>Description</label>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setPreviewMode(!previewMode)}
                  style={{ padding: '4px 8px', fontSize: '12px' }}
                >
                  {previewMode ? <><Edit3 size={14} /> Edit</> : <><Eye size={14} /> Preview</>}
                </button>
              </div>
              {previewMode ? (
                <div className="markdown-preview">
                  {editedTask.description ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mentionMarkdownComponents}>{editedTask.description}</ReactMarkdown>
                  ) : (
                    <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No description yet...</p>
                  )}
                </div>
              ) : (
                <MentionTextarea
                  value={editedTask.description || ''}
                  onChange={(v) => setEditedTask({ ...editedTask, description: v })}
                  members={mentionMembers}
                  rows="8"
                  placeholder={`Write description in Markdown...\n\n**Bold** or __bold__\n*Italic* or _italic_\n- List item\n[Link](url)\n\`code\`\nType @ to mention a team member.`}
                />
              )}
            </div>

            {/* Subtasks (WR-011) — each one carries its own status,
                assignee and due date. */}
            <SubtaskList
              subtasks={editedTask.subtasks || []}
              members={mentionMembers}
              onChange={handleSubtasksChange}
            />
            {subtaskError && (
              <div className="subtask-error" role="alert">{subtaskError}</div>
            )}

            <div className="task-attachments-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ marginBottom: 0 }}>Attachments</label>
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                  <Upload size={16} />
                  {uploading ? 'Uploading…' : 'Upload File'}
                  <input type="file" onChange={handleFileUpload} style={{ display: 'none' }} />
                </label>
              </div>

              {editedTask.attachments && editedTask.attachments.length > 0 ? (
                <>
                  {/* Image Gallery */}
                  {editedTask.attachments.filter(att => att.mimetype?.startsWith('image/')).length > 0 && (
                    <div className="image-gallery">
                      {editedTask.attachments.filter(att => att.mimetype?.startsWith('image/')).map(att => (
                        <div key={att._id} className="gallery-item">
                          <img
                            src={mediaUrl(`/uploads/${att.filename}`)}
                            alt={att.originalName}
                            className="gallery-image"
                          />
                          <div className="gallery-overlay">
                            <a href={mediaUrl(`/uploads/${att.filename}`)} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">
                              <Eye size={14} />
                            </a>
                            <button onClick={() => handleDeleteAttachment(att._id)} className="btn btn-ghost btn-sm">
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <div className="gallery-name">{att.originalName}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Document List */}
                  {editedTask.attachments.filter(att => !att.mimetype?.startsWith('image/')).length > 0 && (
                    <div className="attachments-list">
                      {editedTask.attachments.filter(att => !att.mimetype?.startsWith('image/')).map(att => (
                        <div key={att._id} className="attachment-item">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <div className="attachment-icon">📎</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="attachment-name">{att.originalName}</div>
                              <div className="attachment-meta">
                                {(att.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <a href={mediaUrl(`/uploads/${att.filename}`)} download className="btn btn-ghost btn-sm">
                              <Download size={14} />
                            </a>
                            <button onClick={() => handleDeleteAttachment(att._id)} className="btn btn-ghost btn-sm">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--gray-400)', fontSize: '14px' }}>
                  No attachments yet
                </div>
              )}
            </div>

            <div className="comments-section" ref={commentsRef}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <MessageCircle size={18} />
                <h3>Comments</h3>
                <span className="badge" style={{ background: 'var(--gray-200)', color: 'var(--gray-700)' }}>
                  {comments.length}
                </span>
              </div>

              <form onSubmit={handleAddComment} className="comment-composer">
                <div className="comment-composer-row">
                  <MentionTextarea
                    value={newComment}
                    onChange={setNewComment}
                    members={mentionMembers}
                    inputRef={commentFieldRef}
                    onKeyDown={handleCommentKeyDown}
                    rows={2}
                    className="comment-composer-field"
                    placeholder="Add a comment (Markdown supported, @ to mention)..."
                  />
                  <button
                    type="submit"
                    className="btn btn-primary comment-composer-send"
                    disabled={!newComment.trim()}
                    title="Send comment"
                  >
                    <Send size={16} />
                  </button>
                </div>
                <div className="comment-composer-hint">
                  Enter for a new line · Ctrl+Enter to send
                </div>
              </form>

              <div className="comments-list">
                {comments.map(comment => (
                  <div key={comment._id} className="comment-item">
                    <UserAvatar user={comment.author} className="avatar" style={{ width: '32px', height: '32px', fontSize: '13px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '600', fontSize: '14px' }}>{comment.author.name}</span>
                        {/* WR-023: the comment stays exactly as written; this
                            only notes that its author has since been removed,
                            so nobody waits on a reply that cannot come. */}
                        {comment.author.active === false && (
                          <span className="former-member-tag" title="This person has been removed from the organization">
                            former member
                          </span>
                        )}
                        <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                          {format(new Date(comment.createdAt), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <div className="comment-content markdown-preview">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mentionMarkdownComponents}>{comment.content}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="task-modal-sidebar">
            <div className="sidebar-section" ref={statusControlRef}>
              <label>Work status</label>
              <select
                value={editedTask.status}
                onChange={e => setEditedTask({ ...editedTask, status: e.target.value })}
              >
                <option value="todo">To do</option>
                <option value="inprogress">In progress</option>
                <option value="done">Done</option>
              </select>
            </div>

            <div className="sidebar-section">
              <label>Board column</label>
              <select
                value={editedTask.columnId}
                onChange={e => setEditedTask({ ...editedTask, columnId: e.target.value })}
              >
                {(project.columns || []).map(col => (
                  <option key={col.id} value={col.id}>{col.name}</option>
                ))}
              </select>
            </div>

            <div className="sidebar-section">
              <label>Priority</label>
              <select
                value={editedTask.priority}
                onChange={e => setEditedTask({ ...editedTask, priority: e.target.value })}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div className="sidebar-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={16} />
                Due Date
              </label>
              <input
                type="date"
                value={editedTask.dueDate ? editedTask.dueDate.split('T')[0] : ''}
                onChange={e => setEditedTask({ ...editedTask, dueDate: e.target.value })}
              />
            </div>

            {/* WR-024 B1; saves on its own, see the component */}
            <HorizonTaskSection task={task} project={project} focus={focusSection === 'horizon'} initialHorizonId={horizonId} onScheduleChange={onHorizonScheduleChange} />

            {/* Tags (WR-010). Attaching one is a discrete choice, so it
                saves with the rest of the modal on "Save Changes" — but
                *creating* a tag hits the project immediately, since a tag is
                project-level and other people's pickers should see it. */}
            <div className="sidebar-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <TagIcon size={16} />
                Tags
              </label>
              <TagPicker
                tags={project.tags || []}
                selectedIds={editedTask.tags || []}
                onChange={(tags) => setEditedTask({ ...editedTask, tags })}
                onCreateTag={onCreateTag}
              />
            </div>

            <div className="sidebar-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <Users size={16} />
                Assigned To
              </label>

              {/* Selected assignees */}
              {editedTask.assignedTo && editedTask.assignedTo.length > 0 && (
                <div className="selected-assignees">
                  {editedTask.assignedTo.map(user => (
                    <div key={user._id} className="assignee-tag">
                      <UserAvatar user={user} className="avatar" style={{ width: '20px', height: '20px', fontSize: '10px' }} />
                      <span>{user.name}</span>
                      <button onClick={() => removeAssignee(user._id)} className="remove-assignee">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Dropdown toggle */}
              <div className="assignee-dropdown-wrapper">
                <button
                  type="button"
                  className="assignee-dropdown-toggle"
                  onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                >
                  <Users size={14} />
                  Add assignee...
                </button>

                {/* Dropdown menu */}
                {showAssigneeDropdown && (
                  <div className="assignee-dropdown">
                    <input
                      type="text"
                      className="assignee-search"
                      placeholder="Search members..."
                      value={assigneeSearch}
                      onChange={e => setAssigneeSearch(e.target.value)}
                      autoFocus
                    />
                    <div className="assignee-list">
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map(member => {
                          const isAssigned = editedTask.assignedTo.some(u => u._id === member._id);
                          return (
                            <div
                              key={member._id}
                              className={`assignee-item ${isAssigned ? 'assigned' : ''}`}
                              onClick={() => {
                                toggleAssignee(member._id);
                                if (!isAssigned) {
                                  setShowAssigneeDropdown(false);
                                  setAssigneeSearch('');
                                }
                              }}
                            >
                              <UserAvatar user={member} className="avatar" style={{ width: '28px', height: '28px', fontSize: '12px' }} />
                              <span>{member.name}</span>
                              {isAssigned && <span className="assigned-checkmark">✓</span>}
                            </div>
                          );
                        })
                      ) : (
                        <div className="assignee-empty">No members found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="task-modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TaskModal;
